import { type FC, useCallback, useMemo } from "react";
import { ArrowRight, Check } from "lucide-react";

import { Button } from "@/shared/components/ui/button";
import { OptionCard } from "@/shared/components/ui/OptionCard";
import { Tag } from "@/shared/components/ui/tag";
import { useAppContext } from "@/shared/hooks/useAppContext";
import { useEstateItems } from "@/shared/hooks/useEstateItems";
import type { FlowCustomComponentProps } from "@/shared/constants/flowTypes";

import { DEBT_FIELD_CONFIG } from "./AddOtherDebt";

interface ChecklistItem {
  value: string;
  label: string;
  description: string;
}

function buildChecklistItems(firstName: string): ChecklistItem[] {
  return [
    {
      value: "cosigned_loan",
      label: "Co-signed loan or lease",
      description: `A loan or lease ${firstName} agreed to cover if the other person stopped paying`,
    },
    {
      value: "unpaid_rent",
      label: "Unpaid rent",
      description: `Rent ${firstName} owed on a place they were renting`,
    },
    {
      value: "pending_lawsuits",
      label: "Pending lawsuits",
      description: `An open lawsuit against ${firstName} that hasn't been settled`,
    },
    {
      value: "unfiled_tax_returns",
      label: "Unfiled tax returns",
      description: `Tax returns ${firstName} should have filed but didn't`,
    },
    {
      value: "tax_payment_plan",
      label: "Tax payment plan",
      description: "An active agreement to pay back taxes to the IRS or state",
    },
    {
      value: "estate_inheritance_taxes",
      label: "State estate or inheritance taxes",
      description: "Taxes some states charge based on the estate's value or what heirs receive",
    },
  ];
}

const NONE_VALUE = "none_of_the_above";
const ALL_DEBT_KEYS = buildChecklistItems("").map((i) => i.value);

export const OtherDebtsChecklist: FC<FlowCustomComponentProps> = ({
  onComplete,
  answers,
  setAnswer,
}) => {
  const { person } = useAppContext();
  const firstName = person?.first_name ?? "they";
  const estateId = person?.estate_id;
  const { debts } = useEstateItems(estateId);
  const items = buildChecklistItems(firstName);
  const rawSelected = answers["other_debts_selected"];
  const rawArray: string[] = Array.isArray(rawSelected) ? rawSelected : [];

  const addedKeys = useMemo(() => {
    const existingItemTypes = new Set(debts.map((item) => item.item_type));
    const keys = new Set<string>();
    for (const [key, config] of Object.entries(DEBT_FIELD_CONFIG)) {
      if (existingItemTypes.has(config.itemType)) keys.add(key);
    }
    return keys;
  }, [debts]);

  const selected = useMemo(() => {
    const merged = new Set([...rawArray, ...addedKeys]);
    if (rawArray.includes(NONE_VALUE)) merged.add(NONE_VALUE);
    return [...merged];
  }, [rawArray, addedKeys]);

  const isNoneSelected = selected.includes(NONE_VALUE);

  const toggle = useCallback(
    (value: string) => {
      if (value === NONE_VALUE) {
        if (isNoneSelected) {
          setAnswer("other_debts_selected", [...addedKeys]);
        } else {
          setAnswer("other_debts_selected", [...addedKeys, NONE_VALUE]);
        }
        return;
      }
      if (addedKeys.has(value)) return;
      const next = new Set(selected);
      next.delete(NONE_VALUE);
      if (next.has(value)) {
        next.delete(value);
      } else {
        next.add(value);
      }
      setAnswer("other_debts_selected", [...next]);
    },
    [selected, isNoneSelected, addedKeys, setAnswer],
  );

  const hasSelection = selected.length > 0;

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-3">
        {items.map((item) => {
          const isAdded = addedKeys.has(item.value);
          const isDisabled = isNoneSelected && !isAdded;
          return (
            <div key={item.value} className="relative">
              <OptionCard
                label={item.label}
                description={item.description}
                selected={isAdded || (!isNoneSelected && selected.includes(item.value))}
                onClick={() => !isDisabled && !isAdded && toggle(item.value)}
                variant="checkbox"
                className={
                  isDisabled
                    ? "opacity-50 pointer-events-none"
                    : isAdded
                      ? "pointer-events-none"
                      : ""
                }
              />
              {isAdded && (
                <div className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2">
                  <Tag color="green" icon={Check} iconStroke={2.5}>
                    Added
                  </Tag>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="flex items-center gap-3 py-1">
        <div className="h-px flex-1 bg-border-primary" />
        <span className="text-sm text-secondary">OR</span>
        <div className="h-px flex-1 bg-border-primary" />
      </div>

      <OptionCard
        label="None of the above"
        selected={isNoneSelected}
        onClick={() => toggle(NONE_VALUE)}
        variant="checkbox"
      />

      <div className="flex flex-col items-center gap-3">
        <Button
          variant="brand"
          rounded
          className="w-full max-w-[400px]"
          onClick={() => {
            setAnswer("other_debts_selected", selected);
            onComplete();
          }}
          disabled={!hasSelection}
        >
          Continue <ArrowRight className="h-4 w-4" />
        </Button>
        <Button
          variant="link"
          className="text-secondary"
          onClick={() => {
            setAnswer("other_debts_selected", [...addedKeys, NONE_VALUE]);
            onComplete();
          }}
        >
          Skip for now
        </Button>
      </div>
    </div>
  );
};

export { ALL_DEBT_KEYS, NONE_VALUE };
